import React, { useState } from 'react';
import styled from 'styled-components';
import type { Swiper as SwiperType } from 'swiper';
import SwiperCore, {
  Navigation,
  Scrollbar,
  Mousewheel,
  Pagination,
  Thumbs,
  Keyboard,
} from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.css';
import type { GallerySwiperProps, PopupSwiperProps } from './types';
import {
  pushCloseGalleryAnalytics,
  pushOpenGalleryAnalytics,
  pushShowImageAnalytics,
} from './analytics';

import ImageComponent from './components/ImageComponent';
import { PopupSwiper } from './PopupSwiper';
import { ArrowComponent } from './components/ArrowComponent';
import VideoCover from './components/VideoCover';

SwiperCore.use([
  Navigation,
  Scrollbar,
  Mousewheel,
  Pagination,
  Thumbs,
  Keyboard,
]);

const GallerySwiper = ({
  images,
  video,
  isMobile,
  title,
  hotelId,
  paramsSmallSwiper,
  paramsSwiper,
}: GallerySwiperProps) => {
  let timer = 0;

  const [opened, setOpened] = useState(false);
  const [realIndexSlide, setRealIndexSlide] = useState(0);
  const [swiperGallery, setSwiperGallery] = useState<null | SwiperType>(null);
  const [thumbsSwiper, setThumbsSwiper] = useState<null | SwiperType>(null);
  const [swiperGalleryPopup, setSwiperGalleryPopup] =
    useState<null | SwiperType>(null);

  const showVideo = !!video;

  const openPopup = (index: number, id: number) => {
    const openVideoPopup = showVideo && index === 0;
    if (isMobile && !openVideoPopup) return;

    setRealIndexSlide(index);
    setOpened(true);
    timer = Date.now();

    if (!openVideoPopup) {
      pushOpenGalleryAnalytics(id);
    }
  };

  const closePopup = (id?: number) => {
    setOpened(false);
    timer = 0;

    if (!showVideo || realIndexSlide !== 0) {
      pushCloseGalleryAnalytics(id || 0);
    }
  };

  const changeSlide = () => {
    const isPopupSlideChange =
      swiperGalleryPopup && swiperGallery && !swiperGalleryPopup.destroyed;
    if (!isPopupSlideChange) return;

    const prevIdImage = images[swiperGalleryPopup.previousIndex].id;
    swiperGallery.slideTo(swiperGalleryPopup.activeIndex);

    if (!showVideo || swiperGalleryPopup.previousIndex !== 0) {
      pushShowImageAnalytics(prevIdImage, Date.now() - timer);
    } else {
      const iframeElement = document.querySelector(
        '.yt_player_iframe',
      ) as HTMLIFrameElement | null;
      if (!iframeElement) return;

      iframeElement.contentWindow?.postMessage(
        '{"event":"command","func":"pauseVideo","args":""}',
        '*',
      );
    }

    timer = Date.now();
  };

  const thumbsParam = {
    swiper: thumbsSwiper,
  };

  const swiperPopupProps: PopupSwiperProps = {
    opened,
    video,
    images,
    title,
    hotelId,
    isMobile,
    swiperGalleryPopup,
    realIndexSlide,
    closePopup,
    setSwiperGalleryPopup,
    changeSlide,
  };

  return (
    <>
      <StyledSwiper
        {...paramsSwiper}
        thumbs={thumbsParam}
        initialSlide={realIndexSlide}
        onSwiper={setSwiperGallery}
        isMobile={isMobile}
        isLooped={swiperGallery?.loopedSlides !== undefined}
        className="swiper-container"
      >
        <div className="swiper-wrapper">
          {images.map((image, index) => (
            <SwiperSlide
              key={`slide-${image.id}`}
              onClick={() => openPopup(index, image.id)}
            >
              {showVideo && index === 0 ? (
                <VideoCover title={title} coverImage={video.cover} />
              ) : (
                <ImageComponent
                  src={image.thumbImageX2 ?? ''}
                  title={title}
                  isLazyLoading={index > 6}
                />
              )}
            </SwiperSlide>
          ))}
        </div>
        <ArrowComponent isMobile={isMobile} />
      </StyledSwiper>
      {!isMobile ? (
        <StyledSmallSwiper
          {...paramsSmallSwiper}
          onSwiper={setThumbsSwiper}
          slidesPerView={24}
          watchSlidesProgress
          className="swiper-container"
        >
          <div className="swiper-wrapper">
            {images.map((image, index) => (
              <SwiperSlide key={`slide-${image.id}`}>
                {showVideo && index === 0 ? (
                  <VideoCover title={title} coverImage={video.cover} isSmall />
                ) : (
                  <ImageComponent src={image.smallImage} title={title} />
                )}
              </SwiperSlide>
            ))}
          </div>
        </StyledSmallSwiper>
      ) : null}
      <PopupSwiper {...swiperPopupProps} />
    </>
  );
};

const StyledSwiper = styled(Swiper)<{
  isMobile: boolean;
  isLooped?: boolean;
}>`
  height: 300px;
  cursor: pointer;
  .swiper-slide {
    width: auto;
    ${({ isMobile }) =>
      isMobile &&
      `
      width: 100%;
      height: 100%;
    `}
    img {
      height: 100%;
      ${({ isMobile }) =>
        isMobile &&
        `
        width: 100%;
        object-fit: cover;
      `}
    }
  }
  .swiper-pagination {
    color: #fff;
    ${({ isMobile }) => isMobile && 'margin-bottom: 8px;'}
  }
  .swiper-button-prev,
  .swiper-button-next {
    ${({ isMobile }) =>
      isMobile
        ? `
        bottom: 0px;
        height: 50px;
        margin: auto;
        width: 150px;
        transform: translate(0, 100%);
        `
        : `
        top: 0px;
        height: 100%;
        margin: auto;
        width: 50px;
        `}
  }
  ${({ isMobile }) =>
    isMobile &&
    `
  .swiper-button-prev {
    right: 30%;
  }
  .swiper-button-next {
    left: 30%;
  }
  `}
  ${({ isLooped }) =>
    isLooped &&
    `
    #always-active-button {
      opacity: 1;
      pointer-events: all;
      cursor: pointer;
    }
  `}
`;

const StyledSmallSwiper = styled(Swiper)`
  height: 40px;
  cursor: pointer;
  .swiper-slide {
    width: 60px;
    img {
      object-fit: cover;
      width: 100%;
      height: 100%;
    }
  }
  .swiper-slide-visible {
    opacity: 0.5;
  }
  // Из-за slidesPerView = auto библиотека не может определить активные слайды, поэтому приходится так стилизовать
  .swiper-slide-thumb-active {
    opacity: 1;
  }
  .swiper-slide-thumb-active + .swiper-slide-visible {
    opacity: 1;
  }
  .swiper-slide-thumb-active + .swiper-slide-visible + .swiper-slide-visible {
    opacity: 1;
  }
  .swiper-wrapper {
    width: fit-content;
  }
`;

export default GallerySwiper;
